import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import InputBox from '../FormComponent/InputBox';
import OperatorSelector from './OperatorSelector';
import FilterChecklist from './FilterChecklist';
import ModuleSelector from './ModuleSelector';
import DateFilterInput from './DateFilterInput';
import { debounce } from '../../utils/commonfunctions';
import { getOperatorArity, isUnaryOperator, isSingleValueOperator, isListOperator } from '../../utils/query/operatorArity';
import { parseISO, isValid, format } from 'date-fns';
import { getUserTimezone } from '../../utils/timezone';
import styles from '../../styles/query/FilterDropdown.module.scss';

// Note: getUserTimezone is only used once to normalize timezone at component level.
// After normalization, timezone is passed explicitly as data, never detected again.

/**
 * Filter Dropdown Component
 * 
 * JIRA-style dropdown for filter pills with:
 * - Operator selector (IN/NOT IN)
 * - Search input
 * - Multi-select checklist
 * - Clear and Apply buttons
 * 
 * @param {Object} props
 * @param {Object} props.field - Field metadata from schema
 * @param {string} props.operator - Current operator ('in' | 'notIn')
 * @param {Array} props.selectedValues - Currently selected values
 * @param {Array} props.options - Available options for the field
 * @param {boolean} props.loading - Whether options are loading
 * @param {function} props.onOperatorChange - Handler for operator change
 * @param {function} props.onSelectionChange - Handler for selection change (receives new array)
 * @param {function} props.onApply - Handler for apply button
 * @param {function} props.onClear - Handler for clear button
 * @param {function} props.onClose - Handler to close dropdown
 * @param {Object} props.anchorEl - Element to anchor dropdown to
 */
export function FilterDropdown({
  field,
  operator = '=',
  selectedValues = [],
  options = [],
  loading = false,
  schema,
  onOperatorChange,
  onSelectionChange,
  onApply,
  onClear,
  onClose,
  anchorEl
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [textInputValue, setTextInputValue] = useState('');
  const [localSelectedValues, setLocalSelectedValues] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [invalidDates, setInvalidDates] = useState([]);
  // Date mode state for date fields
  const [dateMode, setDateMode] = useState('absolute'); // 'relative' | 'absolute'
  const [relativeType, setRelativeType] = useState('moreThan'); // 'moreThan' | 'lessThan'
  const [relativeDays, setRelativeDays] = useState('');
  const [absoluteType, setAbsoluteType] = useState('on'); // 'after' | 'on' | 'before' | 'between'
  const [betweenStart, setBetweenStart] = useState('');
  const [betweenEnd, setBetweenEnd] = useState('');
  const [betweenDateError, setBetweenDateError] = useState(null); // Error message for invalid date range
  const [relativeDaysError, setRelativeDaysError] = useState(null); // Error message for invalid relative days input
  const dropdownRef = useRef(null);
  // Track dateValue objects to preserve timezone when passing through
  const betweenStartValueRef = useRef(null);
  const betweenEndValueRef = useRef(null);
  
  // Get timezone for creating new date values (relative dates, etc.)
  // Note: This is for UI intent only. Actual normalization happens in filterPillsToAST.
  const userTimezone = React.useMemo(() => getUserTimezone(), []);
  // Ref to track latest absoluteType to avoid stale closures
  const absoluteTypeRef = useRef('on');
  // Track if user has explicitly selected an absoluteType (to prevent re-initialization)
  const hasUserSelectedTypeRef = useRef(false);
  // Track if user has explicitly selected date mode (relative/absolute)
  // Prevents operator-driven initialization from flipping the UI mode.
  const hasUserSelectedDateModeRef = useRef(false);
  // Track previous field name to detect when switching to a different filter pill
  const prevFieldNameRef = useRef(null);
  
  // Debounced function for text input changes (500ms delay)
  const debouncedTextChangeRef = useRef(
    debounce((value) => {
      // Only call onSelectionChange if we have a non-empty value
      // Empty values will be handled by the filterPillsToAST function (skipped)
      if (value && value.trim()) {
        onSelectionChange([value.trim()]);
      } else {
        // If empty, clear the selection (this will remove the filter from query)
        onSelectionChange([]);
      }
    }, 500)
  );

  // Get default operator from field metadata if not provided
  const currentOperator = React.useMemo(() => {
    if (operator) return operator;
    // Use first operator from field metadata
    if (field?.operators && Array.isArray(field.operators) && field.operators.length > 0) {
      return field.operators[0];
    }
    // Fallback to '=' for text fields, 'in' for enum fields
    return '=';
  }, [operator, field]);

  // Determine if this is a date field
  const isDateField = React.useMemo(() => {
    if (!field) return false;
    return field.kind?.toLowerCase() === 'date';
  }, [field]);

  // Determine if this is a text field (should show text input instead of checkbox list)
  const isTextField = React.useMemo(() => {
    if (!field) return false;
    if (isDateField) return false; // Exclude date fields
    // Text fields: string, text, integer, number, float, or fields without predefined options
    const textKinds = ['string', 'text', 'integer', 'number', 'float', 'decimal'];
    const isTextKind = textKinds.includes(field.kind?.toLowerCase());
    const hasNoOptions = !options || options.length === 0;
    // Common text field names
    const textFieldNames = ['key', 'description', 'title', 'name'];
    const isTextFieldName = textFieldNames.includes(field.name?.toLowerCase());
    
    return isTextKind || (hasNoOptions && isTextFieldName);
  }, [field, options, isDateField]);

  // Date array parsing function (returns { valid, invalid })
  const DATE_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}$/;

  // Supported calendar year bounds for relative dates.
  // We clamp relative "N days ago" so we never generate absurd years
  // that will fail downstream validation (e.g., year 0100).
  const MIN_SUPPORTED_YEAR = 1900;
  const MAX_SUPPORTED_YEAR = 2099;
  // Hard safety cap on relative days (~200 years); exact year bounds are enforced below.
  const MAX_RELATIVE_DAYS = 365 * 200;

  // Helper: compute cutoff date string (YYYY-MM-DD) for N days ago in user's timezone
  // This emits user intent (a calendar day). Normalization happens in filterPillsToAST.
  const computeCutoffDateString = React.useCallback((daysAgo, timezone = userTimezone) => {
    const n = Number(daysAgo);
    // Disallow negatives, non-finite values, and absurdly large ranges
    if (!Number.isFinite(n) || n < 0 || n > MAX_RELATIVE_DAYS) return null;

    // Get current date in the specified timezone
    const now = new Date();
    
    // Format current date in timezone to get today's date
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const parts = formatter.formatToParts(now);
    const year = parseInt(parts.find(p => p.type === 'year').value);
    const month = parseInt(parts.find(p => p.type === 'month').value) - 1; // 0-indexed
    const day = parseInt(parts.find(p => p.type === 'day').value);
    
    // Create date object for today in timezone, then subtract N days
    const todayInTz = new Date(year, month, day);
    todayInTz.setDate(todayInTz.getDate() - n);

    // Clamp by supported year bounds to avoid generating pathological dates
    const resultYear = todayInTz.getFullYear();
    if (resultYear < MIN_SUPPORTED_YEAR || resultYear > MAX_SUPPORTED_YEAR) {
      return null;
    }
    
    // Format as YYYY-MM-DD
    const yearStr = String(resultYear);
    const monthStr = String(todayInTz.getMonth() + 1).padStart(2, '0');
    const dayStr = String(todayInTz.getDate()).padStart(2, '0');
    return `${yearStr}-${monthStr}-${dayStr}`;
  }, [userTimezone]);

  const parseDateArray = React.useCallback((input) => {
    if (!input || !input.trim()) {
      return { valid: [], invalid: [] };
    }
    
    const segments = input.split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    
    const valid = [];
    const invalid = [];
    
    segments.forEach(segment => {
      if (DATE_FORMAT_REGEX.test(segment)) {
        // Use date-fns for validation (timezone-safe)
        const date = parseISO(segment);
        if (isValid(date) && segment === format(date, 'yyyy-MM-dd')) {
          valid.push(segment);
        } else {
          invalid.push(segment);
        }
      } else {
        invalid.push(segment);
      }
    });
    
    return { valid, invalid };
  }, []);

  // Sync absoluteType ref whenever it changes
  useEffect(() => {
    absoluteTypeRef.current = absoluteType;
  }, [absoluteType]);

  // Initialize local state from selectedValues when dropdown opens or values change
  // Also sync when operator changes to ensure single-value operators only have one value
  useEffect(() => {
    let valuesToSet = selectedValues || [];
    const arity = getOperatorArity(currentOperator);
    
    // Normalize values based on operator arity
    if (arity === 0) {
      valuesToSet = [];
    } else if (arity === 1 && valuesToSet.length > 1) {
      valuesToSet = [valuesToSet[0]];
    }
    
    setLocalSelectedValues(valuesToSet);
    
    // Reset user selection flag only when field changes (switching to different filter pill)
    // Don't reset when operator/values change (could be from our own updates)
    const fieldName = field?.name;
    if (fieldName !== prevFieldNameRef.current && prevFieldNameRef.current !== null) {
      hasUserSelectedTypeRef.current = false;
      hasUserSelectedDateModeRef.current = false;
    }
    prevFieldNameRef.current = fieldName;
    
    // Initialize input values for date fields
    // Only initialize dateMode/absoluteType from operator if user hasn't explicitly selected them.
    // This prevents Relative mode from flipping back to Absolute after we resolve relative operators
    // into concrete comparisons (< or >=) for the backend.
    if (isDateField && !hasUserSelectedTypeRef.current && !hasUserSelectedDateModeRef.current) {
      if (currentOperator === 'between' && valuesToSet.length >= 2) {
        setDateMode('absolute');
        setAbsoluteType('between');
        absoluteTypeRef.current = 'between'; // Update ref immediately
        
        // Extract date strings from canonical {date, timezone} format
        // Invariant: values must be {date, timezone}. If not, clear filter gracefully.
        const startValue = valuesToSet[0];
        const endValue = valuesToSet[1];
        if (typeof startValue !== 'object' || !startValue?.date || typeof endValue !== 'object' || !endValue?.date) {
          console.error('Date filter invariant violation: values must be {date: string, timezone: string}', { startValue, endValue });
          // Clear invalid filter gracefully
          onSelectionChange([]);
          return;
        }
        const startDate = startValue.date;
        const endDate = endValue.date;
        
        setBetweenStart(startDate);
        setBetweenEnd(endDate);
        setTextInputValue('');
      } else if (arity === 1 && valuesToSet.length > 0) {
        // Single-date absolute comparison - detect operator and set absoluteType
        setDateMode('absolute');
        
        // Extract date string from canonical {date, timezone} format
        // Invariant: value must be {date, timezone}. If not, clear filter gracefully.
        const firstValue = valuesToSet[0];
        if (typeof firstValue !== 'object' || !firstValue?.date) {
          console.error('Date filter invariant violation: value must be {date: string, timezone: string}', { firstValue });
          // Clear invalid filter gracefully
          onSelectionChange([]);
          return;
        }
        const dateString = firstValue.date;
        setTextInputValue(dateString);
        setBetweenStart('');
        setBetweenEnd('');
        
        // Map operator to absoluteType
        let newAbsoluteType = 'on';
        if (currentOperator === '>') {
          newAbsoluteType = 'after';
        } else if (currentOperator === '<') {
          newAbsoluteType = 'before';
        } else if (currentOperator === '=' || currentOperator === '==') {
          newAbsoluteType = 'on';
        }
        setAbsoluteType(newAbsoluteType);
        absoluteTypeRef.current = newAbsoluteType; // Update ref immediately
      } else if (arity === 'many' && valuesToSet.length > 0) {
        // List-style date input (in / notIn)
        // Extract date strings from canonical {date, timezone} format
        // Invariant: values must be {date, timezone}. If not, clear filter gracefully.
        const dateStrings = [];
        for (const v of valuesToSet) {
          if (typeof v !== 'object' || !v?.date) {
            console.error('Date filter invariant violation: values must be {date: string, timezone: string}', { value: v });
            // Clear invalid filter gracefully
            onSelectionChange([]);
            return;
          }
          dateStrings.push(v.date);
        }
        setTextInputValue(dateStrings.join(', '));
      } else {
        // No values yet - still detect operator to set correct absoluteType
        setTextInputValue('');
        setBetweenStart('');
        setBetweenEnd('');
        
        // Map operator to absoluteType even when no values
        if (currentOperator === 'between') {
          setDateMode('absolute');
          setAbsoluteType('between');
          absoluteTypeRef.current = 'between'; // Update ref immediately
        } else if (arity === 1) {
          setDateMode('absolute');
          let newAbsoluteType = 'on';
          if (currentOperator === '>') {
            newAbsoluteType = 'after';
          } else if (currentOperator === '<') {
            newAbsoluteType = 'before';
          } else if (currentOperator === '>=') {
            // This could be from "less than N days ago" - keep as >=
            newAbsoluteType = 'on'; // We'll handle >= specially if needed
          }
          setAbsoluteType(newAbsoluteType);
          absoluteTypeRef.current = newAbsoluteType; // Update ref immediately
        }
      }
    } else if (isTextField && valuesToSet.length > 0) {
      const firstValue = valuesToSet[0];
      const incoming = typeof firstValue === 'string' ? firstValue : String(firstValue);
      const currentTrimmed = (textInputValue || '').trim();
      // Don't overwrite local input when the only difference is leading/trailing whitespace
      // (e.g. user typed "foo " and we sent "foo" - keep showing "foo " until they blur or change field)
      if (currentTrimmed !== incoming || textInputValue === incoming) {
        setTextInputValue(incoming);
      }
    } else if (isTextField && valuesToSet.length === 0) {
      setTextInputValue('');
    }
  }, [isTextField, isDateField, selectedValues, operator, field?.name]); // Include field name to detect filter pill changes

  // Get available operators from field metadata
  const availableOperators = React.useMemo(() => {
    // Always use operators from field metadata if available
    if (field?.operators && Array.isArray(field.operators) && field.operators.length > 0) {
      return field.operators;
    }
    // Fallback: Default operators based on field type (only if field metadata doesn't have operators)
    if (isTextField) {
      return ['=', '!=', 'contains', 'startsWith', 'endsWith'];
    }
    // For enum/list fields, default to list operators
    return ['in', 'notIn', '=', '!='];
  }, [field, isTextField]);

  const calculatePosition = React.useCallback(() => {
    if (!anchorEl) return;
    
    const rect = anchorEl.getBoundingClientRect();
    const actualDropdownHeight = dropdownRef.current?.offsetHeight || 400;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.max(rect.width, 256);
    
    let left = rect.left;
    if (left + width > viewportWidth) {
      left = rect.right - width;
    }
    if (left < 0) {
      left = 8;
    }
    
    let top = rect.bottom + 4;
    if (top + actualDropdownHeight > viewportHeight) {
      top = rect.top - actualDropdownHeight - 4;
      if (top < 0) {
        top = 8;
      }
    }
    
    setDropdownPosition({
      top,
      left,
      width
    });
  }, [anchorEl]);

  useEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  useEffect(() => {
    if (!anchorEl) return;

    const updatePosition = () => {
      requestAnimationFrame(() => {
        calculatePosition();
      });
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    document.body.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
      document.body.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorEl, calculatePosition, localSelectedValues, searchTerm, textInputValue, dateMode, absoluteType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const operatorDropdown = event.target.closest('[data-operator-dropdown="true"]');
      if (operatorDropdown) {
        return;
      }
      
      const datePickerPopper = event.target.closest('.MuiPickersPopper-root, .MuiDateCalendar-root, [role="dialog"]');
      if (datePickerPopper) {
        return;
      }
      
      const datePickerElement = event.target.closest('[class*="MuiPickers"], [class*="MuiDateCalendar"], [class*="MuiCalendarPicker"]');
      if (datePickerElement) {
        return;
      }
      
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, anchorEl]);

  // Filter options based on search term (only for non-text fields)
  const filteredOptions = React.useMemo(() => {
    if (isTextField) return [];
    if (!searchTerm.trim()) {
      return options;
    }
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = typeof option === 'string' ? option : (option.label || option.name || String(option));
      return label.toLowerCase().includes(term);
    });
  }, [options, searchTerm, isTextField]);

  const handleTextInputChange = (value) => {
    setTextInputValue(value);
    // Debounce the selection change for text inputs to avoid overwhelming backend
    debouncedTextChangeRef.current(value);
  };

  // Date array input handler (for date list operators in / notIn)
  // Emits date strings as user intent. Normalization happens in filterPillsToAST.
  const handleDateArrayInputChange = (value) => {
    setTextInputValue(value);
    const { valid, invalid } = parseDateArray(value);
    setInvalidDates(invalid);
    if (valid.length > 0) {
      // Emit date strings - filterPillsToAST will normalize them to {date, timezone}
      // This is the ONLY place where strings are emitted for dates (user typing comma-separated dates)
      onSelectionChange(valid);
    } else {
      onSelectionChange([]);
    }
  };

  // Handle local selection change (for checklist - triggers query immediately)
  const handleLocalSelectionChange = (newValues) => {
    setLocalSelectedValues(newValues);
    // Call onSelectionChange immediately for enum/list selections (no debounce needed)
    // Only if we have valid values
    if (newValues && Array.isArray(newValues) && newValues.length > 0) {
      onSelectionChange(newValues);
    } else {
      // If empty, still call to clear the filter
      onSelectionChange([]);
    }
  };

  const handleClear = () => {
    // Clear the local state
    setSearchTerm('');
    setTextInputValue('');
    setLocalSelectedValues([]);
    setInvalidDates([]);
    setRelativeDays('');
    setRelativeDaysError(null);
    setBetweenDateError(null);
    // Immediately clear selection and trigger query
    onSelectionChange([]);
  };

  // Apply relative selection: maps UI phrase to absolute comparison
  // Semantics:
  // - More than N days ago  => field < cutoffDate (strictly earlier)
  // - Less than N days ago  => field >= cutoffDate (inclusive)
  // Emits date string as user intent. Normalization happens in filterPillsToAST.
  // This is the ONLY place where strings are emitted for dates (relative date calculation)
  const applyRelativeSelection = (daysValue, type) => {
    const n = Number(daysValue);
    
    // Validate input: must be a non-negative integer
    if (!Number.isFinite(n) || n < 0) {
      setRelativeDaysError('Please enter a valid number of days (0 or greater)');
      onSelectionChange([]);
      return;
    }
    
    if (!Number.isInteger(n)) {
      setRelativeDaysError('Please enter a whole number (decimals are not allowed)');
      onSelectionChange([]);
      return;
    }
    
    // Check if exceeds maximum relative days
    if (n > MAX_RELATIVE_DAYS) {
      const maxYears = Math.floor(MAX_RELATIVE_DAYS / 365);
      setRelativeDaysError(`Maximum allowed is ${MAX_RELATIVE_DAYS.toLocaleString()} days (approximately ${maxYears} years). Dates must be between ${MIN_SUPPORTED_YEAR} and ${MAX_SUPPORTED_YEAR}.`);
      onSelectionChange([]);
      return;
    }
    
    // Compute cutoff date and validate year bounds
    const cutoff = computeCutoffDateString(n);
    if (!cutoff) {
      // computeCutoffDateString returns null if year is outside bounds
      setRelativeDaysError(`Date must be between ${MIN_SUPPORTED_YEAR} and ${MAX_SUPPORTED_YEAR}. This value would result in a date outside this range.`);
      onSelectionChange([]);
      return;
    }

    // Clear any previous errors
    setRelativeDaysError(null);
    
    const op = type === 'lessThan' ? '>=' : '<';
    if (onOperatorChange) {
      onOperatorChange(op);
    }
    
    // Emit date string - filterPillsToAST will normalize it to {date, timezone}
    onSelectionChange([cutoff]);
    setTextInputValue(cutoff);
  };

  const handleRelativeDaysChange = (value) => {
    setRelativeDays(value);
    
    // Clear error when input is cleared
    if (!value || value.trim() === '') {
      setRelativeDaysError(null);
      onSelectionChange([]);
      return;
    }
    
    // Only apply when we have a valid number
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) {
      applyRelativeSelection(n, relativeType);
    } else {
      // Invalid number format
      setRelativeDaysError('Please enter a valid number of days');
      onSelectionChange([]);
    }
  };

  const handleRelativeTypeChange = (type) => {
    hasUserSelectedDateModeRef.current = true;
    setRelativeType(type);
    const n = Number(relativeDays);
    if (relativeDays && Number.isFinite(n) && n >= 0) {
      applyRelativeSelection(n, type);
    }
  };

  const handleAbsoluteSingleDateChange = (dateValue, typeOverride) => {
    // Use ref to get latest absoluteType to avoid stale closures
    const opType = typeOverride || absoluteTypeRef.current;
    
    // DateFilterInput emits {date, timezone} - extract for display
    if (!dateValue || typeof dateValue !== 'object' || !dateValue.date) {
      setTextInputValue('');
      onSelectionChange([]);
      return;
    }
    
    setTextInputValue(dateValue.date);

    let op = '=';
    if (opType === 'after') op = '>';
    if (opType === 'before') op = '<';

    // Always update operator before updating values to ensure correct operator is used
    if (onOperatorChange) {
      onOperatorChange(op);
    }
    
    // Pass through canonical format
    onSelectionChange([dateValue]);
  };

  const handleAbsoluteTypeChange = (type) => {
    // Mark that user has explicitly selected a type - this prevents useEffect from resetting it
    hasUserSelectedTypeRef.current = true;
    hasUserSelectedDateModeRef.current = true;
    setAbsoluteType(type);
    absoluteTypeRef.current = type; // Update ref immediately to avoid stale closures
    
    if (type === 'between') {
      // Switch to between without changing values yet; user will pick dates
      if (onOperatorChange) {
        onOperatorChange('between');
      }
      if (betweenStart && betweenEnd) {
        onSelectionChange([betweenStart, betweenEnd]);
      } else {
        onSelectionChange([]);
      }
    } else {
      // Update operator immediately based on type, even if no date selected yet
      let op = '=';
      if (type === 'after') op = '>';
      if (type === 'before') op = '<';
      
      if (onOperatorChange) {
        onOperatorChange(op);
      }
      
      // If we have a date value, preserve it when switching operators
      // Prefer canonical format from selectedValues, fallback to textInputValue
      let dateValue = null;
      if (selectedValues && selectedValues.length > 0) {
        // Use existing canonical date value from selectedValues
        const firstValue = selectedValues[0];
        if (typeof firstValue === 'object' && firstValue?.date && firstValue?.timezone) {
          dateValue = firstValue;
        }
      }
      
      // If no canonical value found, try to construct from textInputValue
      if (!dateValue && textInputValue) {
        if (typeof textInputValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(textInputValue)) {
          // Convert string to canonical format
          dateValue = { date: textInputValue, timezone: userTimezone };
        }
      }
      
      if (dateValue) {
        // Preserve the date when switching operators
        handleAbsoluteSingleDateChange(dateValue, type);
      } else {
        // Clear selection but operator is already set above
        onSelectionChange([]);
      }
    }
  };

  const handleBetweenDateChange = (which, dateValue) => {
    // Extract date string for display
    const dateString = typeof dateValue === 'string' ? dateValue : (dateValue?.date || '');
    const clean = dateString || '';
    
    // Store the dateValue object to preserve timezone
    if (which === 'start') {
      setBetweenStart(clean);
      betweenStartValueRef.current = typeof dateValue === 'object' && dateValue !== null && dateValue.date
        ? dateValue
        : null;
    } else {
      setBetweenEnd(clean);
      betweenEndValueRef.current = typeof dateValue === 'object' && dateValue !== null && dateValue.date
        ? dateValue
        : null;
    }

    const start = which === 'start' ? clean : betweenStart;
    const end = which === 'end' ? clean : betweenEnd;

    // IMPORTANT UX: Do NOT clear/remove the filter while the user is still completing the range.
    // Only emit to onSelectionChange once BOTH start and end are present.
    // This prevents the dropdown from resetting after typing the first date.
    if (!start || !end) {
      // If the user cleared both, then clear the selection.
      if (!start && !end) {
        setBetweenDateError(null);
        onSelectionChange([]);
      } else {
        // Clear error if one date is missing (user is still typing)
        setBetweenDateError(null);
      }
      return;
    }

    // Validate that end date is not before start date
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        // Invalid date format
        setBetweenDateError(null); // Don't show error for invalid format (DateFilterInput handles that)
        return;
      }
      
      if (endDate < startDate) {
        setBetweenDateError('End date cannot be earlier than start date');
        // Don't emit invalid selection
        return;
      }
      
      // Dates are valid - clear error and apply filter
      setBetweenDateError(null);
      if (onOperatorChange) {
        onOperatorChange('between');
      }
      
      // Pass through canonical {date, timezone} format
      // Invariant: both values must be {date, timezone}. If not, clear filter gracefully.
      if (!betweenStartValueRef.current || !betweenEndValueRef.current) {
        console.error('Date filter invariant violation: both start and end dates must be {date: string, timezone: string}', {
          start: betweenStartValueRef.current,
          end: betweenEndValueRef.current
        });
        // Clear invalid filter gracefully
        onSelectionChange([]);
        return;
      }
      onSelectionChange([betweenStartValueRef.current, betweenEndValueRef.current]);
    } catch (error) {
      // If date parsing fails, don't show error (DateFilterInput handles validation)
      setBetweenDateError(null);
    }
  };

  if (!anchorEl) {
    return null;
  }

  return createPortal(
    <div
      ref={dropdownRef}
      className={styles.filterDropdown}
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        zIndex: 40
      }}
    >
      {/* Operator Selector - JIRA style at top */}
      {/* Hide operator selector for date fields with Relative/Absolute UI (show only for unary operators like isEmpty/isNotEmpty) */}
      {/* For date fields, only show operator selector for unary operators; all others use Relative/Absolute UI */}
      {(!isDateField || isUnaryOperator(currentOperator)) && (
        <div className={styles.operatorSection}>
          <OperatorSelector
            operator={currentOperator}
            availableOperators={availableOperators}
            fieldLabel={field?.label || field?.name || ''}
            onOperatorChange={onOperatorChange}
          />
        </div>
      )}

      {/* Date Field Input */}
      {isDateField && isUnaryOperator(currentOperator) ? (
        <div className={styles.unaryOperatorMessage}>
          <p>This operator does not require a date value.</p>
        </div>
      ) : isDateField && isListOperator(currentOperator) && currentOperator !== 'between' ? (
        // Only show text input for in/notIn operators, not for 'between' which uses Relative/Absolute UI
        <div className={styles.textInputSection}>
          <InputBox
            size="small"
            placeholder="Enter dates (comma-separated, YYYY-MM-DD)..."
            value={textInputValue}
            onChange={(e) => handleDateArrayInputChange(e.target.value)}
            customClassName={styles.textInput}
            autoFocus
          />
          <p className={styles.helperText}>
            Format: YYYY-MM-DD, YYYY-MM-DD (e.g., 2024-01-01, 2024-01-15)
          </p>
          {invalidDates.length > 0 && (
            <p className={styles.errorText}>
              Invalid date format: {invalidDates.join(', ')}. Please use YYYY-MM-DD format.
            </p>
          )}
        </div>
      ) : isDateField ? (
        <div className={styles.dateModeSection}>
          {/* Mode toggle */}
          <div className={styles.dateModeToggle}>
            <button
              type="button"
              className={`${styles.dateModeButton} ${dateMode === 'relative' ? styles.dateModeButtonActive : ''}`}
              onClick={() => {
                hasUserSelectedDateModeRef.current = true;
                setDateMode('relative');
                setRelativeDaysError(null); // Clear error when switching modes
              }}
            >
              Relative
            </button>
            <button
              type="button"
              className={`${styles.dateModeButton} ${dateMode === 'absolute' ? styles.dateModeButtonActive : ''}`}
              onClick={() => {
                hasUserSelectedDateModeRef.current = true;
                setDateMode('absolute');
                setRelativeDaysError(null); // Clear error when switching modes
              }}
            >
              Absolute
            </button>
          </div>

          {dateMode === 'relative' ? (
            <div className={styles.relativeSection}>
              <div className={styles.relativeOptions}>
                <label>
                  <input
                    type="radio"
                    name="relative-type"
                    value="moreThan"
                    checked={relativeType === 'moreThan'}
                    onChange={() => handleRelativeTypeChange('moreThan')}
                  />
                  <span>More than</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="relative-type"
                    value="lessThan"
                    checked={relativeType === 'lessThan'}
                    onChange={() => handleRelativeTypeChange('lessThan')}
                  />
                  <span>Less than</span>
                </label>
              </div>
              <div className={styles.relativeInputRow}>
                <InputBox
                  size="small"
                  placeholder="0"
                  value={relativeDays}
                  onChange={(e) => handleRelativeDaysChange(e.target.value)}
                  className={styles.relativeDaysInputWrapper}
                  customClassName={styles.relativeDaysInput}
                />
                <span className={styles.relativeSuffix}>
                 days
                </span>
              </div>
              {relativeDaysError && (
                <p className={styles.errorText}>
                  {relativeDaysError}
                </p>
              )}
            </div>
          ) : (
            <div className={styles.absoluteSection}>
              <div className={styles.absoluteOptions}>
                <label>
                  <input
                    type="radio"
                    name="absolute-type"
                    value="after"
                    checked={absoluteType === 'after'}
                    onChange={() => handleAbsoluteTypeChange('after')}
                  />
                  <span>After</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="absolute-type"
                    value="on"
                    checked={absoluteType === 'on'}
                    onChange={() => handleAbsoluteTypeChange('on')}
                  />
                  <span>On</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="absolute-type"
                    value="before"
                    checked={absoluteType === 'before'}
                    onChange={() => handleAbsoluteTypeChange('before')}
                  />
                  <span>Before</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="absolute-type"
                    value="between"
                    checked={absoluteType === 'between'}
                    onChange={() => handleAbsoluteTypeChange('between')}
                  />
                  <span>Between</span>
                </label>
              </div>

              {absoluteType === 'between' ? (
                <div className={styles.betweenSection}>
                  <div className={styles.betweenInputsRow}>
                    <DateFilterInput
                      value={betweenStart ? { date: betweenStart, timezone: userTimezone } : null}
                      onChange={(dateString) => handleBetweenDateChange('start', dateString)}
                      placeholder={`Start ${field?.label || field?.name || 'date'}...`}
                    />
                    <span className={styles.betweenSeparator}>and</span>
                    <DateFilterInput
                      value={betweenEnd ? { date: betweenEnd, timezone: userTimezone } : null}
                      onChange={(dateString) => handleBetweenDateChange('end', dateString)}
                      placeholder={`End ${field?.label || field?.name || 'date'}...`}
                    />
                  </div>
                  {betweenDateError ? (
                    <p className={styles.errorText}>
                      {betweenDateError}
                    </p>
                  ) : (
                    <p className={styles.helperText}>
                      Between is inclusive of both start and end dates (>= start AND &lt;= end).
                    </p>
                  )}
                </div>
              ) : (
                <div className={styles.dateInputSection}>
                  <DateFilterInput
                    value={textInputValue ? { date: textInputValue, timezone: userTimezone } : null}
                    onChange={(dateString) => handleAbsoluteSingleDateChange(dateString)}
                    placeholder={`Select ${field?.label || field?.name || 'date'}...`}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ) : isTextField ? (
        <div className={styles.textInputSection}>
          <InputBox
            size="small"
            placeholder={isUnaryOperator(currentOperator) 
              ? "This operator does not require a value" 
              : `Enter ${field?.label || field?.name || 'value'}...`}
            value={textInputValue}
            onChange={(e) => handleTextInputChange(e.target.value)}
            customClassName={styles.textInput}
            autoFocus={!isUnaryOperator(currentOperator)}
            disabled={isUnaryOperator(currentOperator)}
          />
        </div>
      ) : field?.name === 'module' ? (
        // Module selector with tree view
        <div className={styles.moduleSelectorSection}>
          {loading ? (
            <div className={styles.loadingState}>
              <p className={styles.loadingText}>Loading modules...</p>
            </div>
          ) : (
            <ModuleSelector
              options={options}
              selectedValues={localSelectedValues}
              onSelectionChange={handleLocalSelectionChange}
              operator={currentOperator}
            />
          )}
        </div>
      ) : (
        <>
          {/* Search Input for list fields */}
          <div className={styles.searchSection}>
            <div className={styles.searchContainer}>
              <MagnifyingGlassIcon className={styles.searchIcon} />
              <InputBox
                size="small"
                placeholder={isUnaryOperator(currentOperator)
                  ? "This operator does not require a value"
                  : `Search ${field?.label || field?.name || 'options'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                customClassName={styles.searchInput}
                disabled={isUnaryOperator(currentOperator)}
              />
            </div>
          </div>

          {/* Checklist for list fields */}
          <div className={styles.checklistSection}>
            {isUnaryOperator(currentOperator) ? (
              <div className={styles.unaryOperatorMessage}>
                <p>This operator does not require a value.</p>
              </div>
            ) : loading ? (
              <div className={styles.loadingState}>
                <p className={styles.loadingText}>Loading options...</p>
              </div>
            ) : (
              <FilterChecklist
                options={filteredOptions}
                selectedValues={localSelectedValues}
                onSelectionChange={handleLocalSelectionChange}
                field={field}
                operator={currentOperator}
              />
            )}
          </div>
        </>
      )}

      {/* Footer Actions - Only Clear button for instant apply */}
      <div className={styles.footerSection}>
        <button
          type="button"
          className={styles.clearButton}
          onClick={handleClear}
        >
          Clear
        </button>
      </div>
    </div>,
    document.body
  );
}

export default FilterDropdown;

